import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Star, Copy, ArrowLeft, Loader2, MessageSquare, Calendar, Mail,
  AlertTriangle, Filter, X, TrendingUp, TrendingDown,
  Minus, ChevronLeft, ChevronRight, Download, Pencil, Power, Lightbulb,
  QrCode, CheckCircle, ExternalLink,
} from 'lucide-react';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';
import FunnelFlow from '../components/FunnelFlow';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip as RechartsTooltip,
} from 'recharts';


// ─── Bewertungsverlauf-Chart ──────────────────────────────────────────────────
function buildChartData(reviews, range) {
  const now = new Date();
  const days = range === 'woche' ? 7 : range === 'monat' ? 30 : 365;
  const buckets = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = range === 'jahr'
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets[key] = 0;
  }
  reviews.forEach(r => {
    const d = new Date(r.createdAt);
    const key = range === 'jahr'
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (key in buckets) buckets[key]++;
  });
  const result = Object.entries(buckets);
  // For year: deduplicate to monthly buckets
  if (range === 'jahr') {
    const monthly = {};
    result.forEach(([k, v]) => { monthly[k] = (monthly[k] || 0) + v; });
    return Object.entries(monthly).map(([date, Bewertungen]) => ({ date, Bewertungen }));
  }
  // For week/month: show every Nth label to avoid crowding
  return result.map(([date, Bewertungen]) => ({ date, Bewertungen }));
}

function LinkChart({ reviews, range }) {
  const chartData = buildChartData(reviews, range);
  const hasData = chartData.some(d => d.Bewertungen > 0);

  if (!hasData) {
    return (
      <div className="h-36 flex items-center justify-center text-sm text-muted-foreground">
        Noch keine Bewertungsdaten für diesen Zeitraum
      </div>
    );
  }
  const tickInterval = range === 'woche' ? 0 : range === 'monat' ? 4 : 1;

  return (
    <ResponsiveContainer width="100%" height={144}>
      <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
        <defs>
          <linearGradient id="linkChartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={tickInterval} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
        <RechartsTooltip
          content={({ active, payload }) =>
            active && payload?.length
              ? <div className="text-xs bg-background border border-border rounded px-2 py-1 shadow space-y-0.5">
                  <div className="text-muted-foreground">{payload[0].payload.date}</div>
                  <div className="font-medium">{payload[0].value} Bewertung{payload[0].value !== 1 ? 'en' : ''}</div>
                </div>
              : null
          }
        />
        <Area
          type="monotone" dataKey="Bewertungen"
          stroke="#3b82f6" strokeWidth={1.5}
          fill="url(#linkChartGrad)" dot={false} isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function HVLinkDetail() {
  const { linkId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth('hv_user');
  const [links, setLinks] = useState([]);
  const [link, setLink] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalReviews, setTotalReviews] = useState(0);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [linkSettings, setLinkSettings] = useState({
    location: '',
    platform_urls: { google: '', provenexpert: '', trustpilot: '' },
  });
  const [useCustomUrls, setUseCustomUrls] = useState(false);
  const [funnelStats, setFunnelStats] = useState(null);
  const [funnelFlow, setFunnelFlow] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [selectedReviewIds, setSelectedReviewIds] = useState(new Set());
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [chartRange, setChartRange] = useState('monat'); // woche | monat | jahr
  const initialTab = searchParams.get('tab') || 'uebersicht';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) loadData(); }, [user, linkId, page]);

  // Sync settings state when link + user load
  useEffect(() => {
    if (!link) return;
    const tenantUrls = user?.tenant?.platform_urls || {};
    const linkUrls = link.platform_urls || {};
    // A link has custom URLs if any of its values differ from the tenant defaults
    const hasCustom = Object.entries(linkUrls).some(
      ([k, v]) => v && v !== (tenantUrls[k] || '')
    );
    setUseCustomUrls(hasCustom);
    setLinkSettings({
      location: link.location || '',
      platform_urls: {
        google: linkUrls.google || '',
        provenexpert: linkUrls.provenexpert || '',
        trustpilot: linkUrls.trustpilot || '',
      },
    });
  }, [link, user]);

  const loadData = async () => {
    try {
      const [linksRes, reviewsRes, funnelRes, flowRes] = await Promise.all([
        hvApi.getLinks(),
        hvApi.getLinkReviews(linkId, { skip: (page - 1) * pageSize, limit: pageSize }),
        hvApi.getFunnelStats(linkId).catch(() => null),
        hvApi.getFunnelFlow(linkId).catch(() => null),
      ]);
      const currentLink = linksRes.data.find(l => l.id === linkId);
      setLink(currentLink);
      setLinks(linksRes.data);
      setReviews(reviewsRes.data.reviews);
      setTotalReviews(reviewsRes.data.total || reviewsRes.data.reviews.length);
      if (funnelRes) setFunnelStats(funnelRes.data);
      if (flowRes) setFunnelFlow(flowRes.data);
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = () => `${window.location.origin}/r/${user?.tenant?.slug}/${link?.slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(getFullUrl());
    toast.success('Link kopiert!');
  };

  const copyIframe = () => {
    const code = `<iframe src="${getFullUrl()}" width="100%" height="600" frameborder="0" style="border-radius:12px"></iframe>`;
    navigator.clipboard.writeText(code);
    toast.success('iFrame-Code kopiert!');
  };

  const downloadQRPng = () => {
    const svg = document.querySelector('[data-qr-hidden]');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 400; canvas.height = 400;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `qr-${link?.slug || 'code'}.png`;
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const downloadQRSvg = () => {
    const svg = document.querySelector('[data-qr-hidden]');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `qr-${link?.slug || 'code'}.svg`;
    a.click();
  };

  const openReviewDetail = (review) => {
    setSelectedReview(review);
    setSheetOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!link) return;
    setTogglingStatus(true);
    try {
      const newStatus = link.status === 'active' ? 'disabled' : 'active';
      await hvApi.updateLink(linkId, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Link aktiviert' : 'Link deaktiviert');
      loadData();
    } catch { toast.error('Fehler beim Aktualisieren'); }
    finally { setTogglingStatus(false); }
  };

  const openRename = () => { setRenameName(link?.name || ''); setRenameOpen(true); };

  const handleRename = async () => {
    if (!renameName.trim()) return;
    try {
      await hvApi.updateLink(linkId, { name: renameName });
      toast.success('Link umbenannt');
      setRenameOpen(false);
      loadData();
    } catch { toast.error('Fehler beim Umbenennen'); }
  };

  const toggleReviewSelect = (id) => {
    setSelectedReviewIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedReviewIds.size === filteredReviews.length) {
      setSelectedReviewIds(new Set());
    } else {
      setSelectedReviewIds(new Set(filteredReviews.map(r => r.id)));
    }
  };

  const handleBulkStatus = async (hv_status) => {
    setBulkStatusLoading(true);
    try {
      await Promise.all([...selectedReviewIds].map(id => hvApi.setReviewStatus(id, hv_status)));
      toast.success(`${selectedReviewIds.size} Bewertung(en) aktualisiert`);
      setSelectedReviewIds(new Set());
      loadData();
    } catch { toast.error('Fehler beim Aktualisieren'); }
    finally { setBulkStatusLoading(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await hvApi.updateLink(linkId, {
        location: linkSettings.location || null,
        platform_urls: linkSettings.platform_urls,
        target_platforms: Object.entries(linkSettings.platform_urls)
          .filter(([, url]) => url.trim())
          .map(([platform]) => platform)
          .concat(['google'])
          .filter((v, i, a) => a.indexOf(v) === i),
      });
      toast.success('Einstellungen gespeichert');
      loadData();
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSavingSettings(false); }
  };

  const getSentiment = (rating) => {
    if (rating >= 4) return { label: 'Positiv', icon: TrendingUp, color: 'text-green-600' };
    if (rating === 3) return { label: 'Neutral', icon: Minus, color: 'text-yellow-600' };
    return { label: 'Kritisch', icon: TrendingDown, color: 'text-red-600' };
  };

  const getCategoryAverage = (review) => {
    if (!review.categories || review.categories.length === 0) return null;
    const sum = review.categories.reduce((acc, c) => acc + c.rating, 0);
    return (sum / review.categories.length).toFixed(1);
  };

  const filteredReviews = reviews.filter(r => {
    if (ratingFilter !== 'all' && r.overall_rating !== parseInt(ratingFilter)) return false;
    if (typeFilter !== 'all' && r.path !== typeFilter) return false;
    return true;
  });

  const hasActiveFilters = ratingFilter !== 'all' || typeFilter !== 'all';
  const resetFilters = () => { setRatingFilter('all'); setTypeFilter('all'); };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${i <= rating ? 'fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]' : 'text-gray-300'}`} />
      ))}
    </div>
  );

  // ─── Conversion Insights ──────────────────────────────────────────────────
  const convincedUp = reviews.filter(r => {
    const avg = getCategoryAverage(r);
    return avg && parseFloat(avg) > r.overall_rating;
  });
  const positiveReviews = reviews.filter(r => r.path === 'positive');
  const wentExternal = reviews.filter(r =>
    r.chosen_platform || r.external_platform || r.external?.platform
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!link) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Link nicht gefunden</h1>
            <Link to="/app/links"><Button variant="secondary">Zurück zu Links</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav />

      {/* Hidden QR always in DOM for download functions */}
      <div className="sr-only" aria-hidden>
        <QRCodeSVG data-qr-hidden value={getFullUrl()} size={400} level="M" />
      </div>

      <main className="container-main py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/app/links" className="hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Links
          </Link>
          <span>/</span>
          <span className="text-foreground">{link.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-semibold">{link.name}</h1>
              <button onClick={openRename} className="text-muted-foreground hover:text-foreground transition-colors" title="Umbenennen">
                <Pencil className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-muted-foreground text-sm">/{link.slug}</p>
              <Badge variant={link.status === 'active' ? 'default' : 'secondary'}>
                {link.status === 'active' ? 'Aktiv' : 'Inaktiv'}
              </Badge>
            </div>
          </div>
        </div>

        {/* ─── Tabs ─────────────────────────────────────────────────────── */}
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
            <TabsTrigger value="bewertungen">
              Bewertungen
              {totalReviews > 0 && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">{totalReviews}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="einbetten">Einbetten</TabsTrigger>
            <TabsTrigger value="einstellungen">Einstellungen</TabsTrigger>
          </TabsList>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* TAB 1: ÜBERSICHT                                               */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="uebersicht" className="space-y-6">
            {/* Prominent URL bar */}
            <div className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/40">
              <span className="text-xs text-muted-foreground shrink-0">Bewertungslink:</span>
              <a href={getFullUrl()} target="_blank" rel="noopener noreferrer"
                className="flex-1 text-sm font-mono text-primary truncate select-all hover:underline">
                {getFullUrl()}
              </a>
              <Button size="sm" variant="outline" onClick={() => document.querySelector('[value="einbetten"]')?.click()}
                className="shrink-0 h-7 px-2.5 text-xs gap-1.5">
                <QrCode className="w-3 h-3" />
                QR-Code
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold">{reviews.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Positiv (4–5★)</p>
                  <p className="text-2xl font-bold text-green-600">{reviews.filter(r => r.overall_rating >= 4).length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Kritisch (1–3★)</p>
                  <p className="text-2xl font-bold text-yellow-600">{reviews.filter(r => r.overall_rating <= 3).length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Durchschnitt</p>
                  <p className="text-2xl font-bold">
                    {reviews.length > 0
                      ? (reviews.reduce((s, r) => s + r.overall_rating, 0) / reviews.length).toFixed(1)
                      : '–'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Was Immoverita bewirkt hat — Stacked Bar */}
            {reviews.length > 0 && (() => {
              const total = reviews.length;
              const pos = reviews.filter(r => r.overall_rating >= 4).length;
              const neg = total - pos;
              const posPct = Math.round((pos / total) * 100);
              const negPct = 100 - posPct;
              const withCategories = reviews.filter(r => r.categories && r.categories.length > 0);
              // Echte Zahlen aus dem Backend bevorzugen; sonst aus der geladenen Seite ableiten.
              // (Vorher wurde „Zu Portalen" fälschlich mit der Anzahl positiver Reviews aufgefüllt.)
              const externalCount = funnelFlow?.external_clicks ?? wentExternal.length;
              const improvedCount = funnelFlow?.improved ?? convincedUp.length;
              const stats2 = [
                { value: improvedCount, label: 'Verbessert', color: 'text-green-700' },
                { value: funnelFlow ? (funnelFlow.refine.from_positive + funnelFlow.refine.from_critical) : withCategories.length, label: 'Differenziert', color: 'text-blue-700' },
                { value: externalCount, label: 'Zu Portalen', color: 'text-violet-700' },
                { value: neg, label: 'Abgefangen', color: 'text-orange-700' },
              ];
              return (
                <Card className="card-shadow">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Was Immoverita bewirkt hat</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Messbare Auswirkungen des differenzierten Bewertungsprozesses</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Stacked Bar */}
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                        <span className="font-medium text-green-700">{pos} Positiv ({posPct}%)</span>
                        <span className="font-medium text-red-700">{neg} Kritisch ({negPct}%)</span>
                      </div>
                      <div className="h-5 rounded-full overflow-hidden flex gap-0.5">
                        {pos > 0 && (
                          <div className="bg-green-500 rounded-l-full transition-all" style={{ width: `${posPct}%` }} />
                        )}
                        {neg > 0 && (
                          <div className="bg-red-400 rounded-r-full flex-1 transition-all" />
                        )}
                      </div>
                      {(funnelFlow?.converted_up ?? 0) > 0 && (
                        <p className="text-[11px] text-green-600 mt-1.5 font-medium">
                          ↑ {funnelFlow.converted_up} kritische Impuls-{funnelFlow.converted_up === 1 ? 'Bewertung' : 'Bewertungen'} wurden nach der Detailabfrage positiv
                        </p>
                      )}
                    </div>
                    {/* Compact stats row */}
                    <div className="grid grid-cols-4 gap-2">
                      {stats2.map(({ value, label, color }) => (
                        <div key={label} className="text-center p-2 rounded-lg bg-muted/50">
                          <p className={`text-xl font-bold ${color}`}>{value}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">{label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Bewertungsverlauf */}
            <Card className="card-shadow">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base">Bewertungsverlauf</CardTitle>
                  <div className="flex gap-1">
                    {[
                      { key: 'woche', label: 'Woche' },
                      { key: 'monat', label: 'Monat' },
                      { key: 'jahr', label: 'Jahr' },
                    ].map(r => (
                      <button key={r.key} onClick={() => setChartRange(r.key)}
                        className={`px-2.5 py-1 rounded text-xs border transition-colors ${chartRange === r.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <LinkChart reviews={reviews} range={chartRange} />
              </CardContent>
            </Card>

            {/* Funnel Klickfluss */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-base">Funnel-Klickfluss</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Wie weit kommen Bewerter im Funnel?</p>
              </CardHeader>
              <CardContent>
                <FunnelFlow flow={funnelFlow} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* TAB 2: BEWERTUNGEN                                             */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="bewertungen">
            {/* Filters + Export */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Bewertung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bewertungen</SelectItem>
                  <SelectItem value="5">5 Sterne</SelectItem>
                  <SelectItem value="4">4 Sterne</SelectItem>
                  <SelectItem value="3">3 Sterne</SelectItem>
                  <SelectItem value="2">2 Sterne</SelectItem>
                  <SelectItem value="1">1 Stern</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Typ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="positive">Positiv</SelectItem>
                  <SelectItem value="negative">Kritisch</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Filter zurücksetzen
                </Button>
              )}
              <div className="ml-auto">
                <Button
                  variant="outline" size="sm"
                  onClick={async () => {
                    try {
                      const resp = await hvApi.exportReviews(linkId);
                      const url = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `bewertungen_${link?.name || 'export'}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('CSV exportiert');
                    } catch { toast.error('Export fehlgeschlagen'); }
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV Export
                </Button>
              </div>
            </div>

            <Card className="card-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Bewertungen
                    {hasActiveFilters && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        ({filteredReviews.length} von {reviews.length})
                      </span>
                    )}
                  </CardTitle>
                  {selectedReviewIds.size > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{selectedReviewIds.size} ausgewählt</span>
                      <Button size="sm" variant="outline" onClick={() => handleBulkStatus('reagiert')} disabled={bulkStatusLoading} className="text-green-700 border-green-200 hover:bg-green-50">Reagiert</Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkStatus('archiviert')} disabled={bulkStatusLoading} className="text-slate-500">Archivieren</Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkStatus('offen')} disabled={bulkStatusLoading}>Offen</Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedReviewIds(new Set())}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {filteredReviews.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      {hasActiveFilters ? 'Keine Bewertungen für diesen Filter' : 'Noch keine Bewertungen für diesen Link'}
                    </p>
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={resetFilters} className="mt-2">Filter zurücksetzen</Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <input type="checkbox" className="rounded border-border"
                            checked={selectedReviewIds.size === filteredReviews.length && filteredReviews.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead>Bewertung</TableHead>
                        <TableHead>Stimmung</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReviews.map((review) => {
                        const sentiment = getSentiment(review.overall_rating);
                        const isSelected = selectedReviewIds.has(review.id);
                        const hvStatus = review.hv_status || 'offen';
                        return (
                          <TableRow key={review.id} className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <input type="checkbox" className="rounded border-border"
                                checked={isSelected} onChange={() => toggleReviewSelect(review.id)}
                              />
                            </TableCell>
                            <TableCell onClick={() => openReviewDetail(review)}>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                {format(new Date(review.createdAt), 'dd.MM.yyyy', { locale: de })}
                              </div>
                            </TableCell>
                            <TableCell onClick={() => openReviewDetail(review)}>{renderStars(review.overall_rating)}</TableCell>
                            <TableCell onClick={() => openReviewDetail(review)}>
                              <div className={`flex items-center gap-1 ${sentiment.color}`}>
                                <sentiment.icon className="w-4 h-4" />
                                <span className="text-sm">{sentiment.label}</span>
                              </div>
                            </TableCell>
                            <TableCell onClick={() => openReviewDetail(review)}>
                              <Badge variant={review.path === 'positive' ? 'default' : 'secondary'}>
                                {review.path === 'positive' ? 'Positiv' : 'Kritik'}
                              </Badge>
                            </TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Select value={hvStatus} onValueChange={async (val) => {
                                try { await hvApi.setReviewStatus(review.id, val); loadData(); }
                                catch { toast.error('Fehler beim Aktualisieren'); }
                              }}>
                                <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="offen">Offen</SelectItem>
                                  <SelectItem value="reagiert">Reagiert</SelectItem>
                                  <SelectItem value="archiviert">Archiviert</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => openReviewDetail(review)}>Details</Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </div>
                )}
                {totalReviews > pageSize && (
                  <div className="flex items-center justify-between pt-4 border-t mt-4">
                    <p className="text-sm text-muted-foreground">Seite {page} von {Math.ceil(totalReviews / pageSize)}</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                        <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(Math.ceil(totalReviews / pageSize), p + 1))} disabled={page >= Math.ceil(totalReviews / pageSize)}>
                        Weiter <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* TAB 3: EINBETTEN                                               */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="einbetten" className="space-y-6">

            {/* Immoverita Tipp: QR */}
            <div className="rounded-xl p-5"
                 style={{
                   background: 'linear-gradient(135deg, #f0fdf8 0%, #eff6ff 100%) padding-box, linear-gradient(135deg, rgba(16,185,129,0.3), rgba(59,130,246,0.3)) border-box',
                   border: '2px solid transparent',
                 }}>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
                  <Lightbulb className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400 block mb-0.5">Immoverita Tipp</span>
                  <p className="text-sm font-medium text-slate-700 leading-snug">
                    Drucken Sie den QR-Code auf Anschreiben, Flyer oder Aushänge — Bewerter können direkt mit dem Smartphone scannen und bewerten.
                  </p>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Sie können den QR-Code kopieren oder runterladen und beispielsweise in einen Serienbrief einfügen.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    <strong>SVG</strong> empfohlen für großflächigen Einsatz (Druck, Plakate) — <strong>PNG</strong> für digitale Nutzung (E-Mail, Web).
                  </p>
                </div>
              </div>

              {/* QR Preview */}
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <div className="flex-shrink-0 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <QRCodeSVG value={getFullUrl()} size={140} level="M" />
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  <Button variant="outline" className="justify-start gap-2 w-full sm:w-48" onClick={downloadQRSvg}>
                    <Download className="w-4 h-4" />
                    SVG herunterladen
                  </Button>
                  <Button variant="outline" className="justify-start gap-2 w-full sm:w-48" onClick={downloadQRPng}>
                    <Download className="w-4 h-4" />
                    PNG herunterladen
                  </Button>
                </div>
              </div>
            </div>

            {/* Direktlink — gleicher Look wie URL-Bar in Übersicht */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-base">Direktlink</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Teilen Sie diesen Link direkt mit Bewertern.</p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/40">
                  <a href={getFullUrl()} target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-sm font-mono text-primary truncate select-all hover:underline">
                    {getFullUrl()}
                  </a>
                  <Button size="sm" variant="outline" onClick={copyLink} className="shrink-0 h-7 px-2.5 text-xs gap-1.5">
                    <Copy className="w-3 h-3" />
                    Kopieren
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* iFrame */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-base">iFrame</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Binden Sie den Bewertungs-Funnel direkt auf Ihrer Website ein.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <code className="block p-3 bg-muted rounded-lg text-xs font-mono break-all whitespace-pre-wrap">
                  {`<iframe src="${getFullUrl()}" width="100%" height="600" frameborder="0" style="border-radius:12px"></iframe>`}
                </code>
                <Button variant="outline" className="w-full" onClick={copyIframe}>
                  <Copy className="w-4 h-4 mr-2" />
                  Code kopieren
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* TAB 4: EINSTELLUNGEN                                           */}
          {/* ══════════════════════════════════════════════════════════════ */}
          <TabsContent value="einstellungen" className="space-y-6">
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-base">Link-Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="text-sm font-medium block mb-1">Bewertungs-Plattformen (URLs)</label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Standard-URLs aus den{' '}
                    <Link to="/app/settings/company" className="underline text-primary">Unternehmenseinstellungen</Link>.
                    Klicke ein Feld an, um es für diesen Link individuell anzupassen.
                  </p>

                  {(() => {
                    const tenantUrls = user?.tenant?.platform_urls || {};
                    const hasTenantUrls = Object.values(tenantUrls).some(v => v);
                    return (
                      <>
                        {!hasTenantUrls && (
                          <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                            Noch keine Standard-URLs hinterlegt. Bitte in den{' '}
                            <Link to="/app/settings/company" className="underline font-medium">Unternehmenseinstellungen</Link>{' '}
                            eingeben.
                          </div>
                        )}

                        <div className="space-y-2">
                          {[
                            { key: 'google', label: 'Google', placeholder: 'https://g.page/r/...' },
                            { key: 'provenexpert', label: 'ProvenExpert', placeholder: 'https://www.provenexpert.com/...' },
                            { key: 'trustpilot', label: 'Trustpilot', placeholder: 'https://www.trustpilot.com/review/...' },
                          ].map(({ key, label, placeholder }) => {
                            const tenantVal = tenantUrls[key] || '';
                            const customVal = linkSettings.platform_urls[key] || '';
                            const isCustomized = !!customVal && customVal !== tenantVal;
                            const displayVal = customVal || tenantVal;
                            return (
                              <div key={key}>
                                <label className="text-xs font-medium mb-1 flex items-center gap-1.5 text-muted-foreground">
                                  {label}
                                  {isCustomized && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 font-semibold">Individuell</span>}
                                  {!isCustomized && tenantVal && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Standard</span>}
                                </label>
                                <div className="relative group">
                                  <Input
                                    value={displayVal}
                                    onChange={(e) => {
                                      setUseCustomUrls(true);
                                      setLinkSettings(s => ({
                                        ...s, platform_urls: { ...s.platform_urls, [key]: e.target.value },
                                      }));
                                    }}
                                    placeholder={placeholder}
                                    className={!isCustomized && tenantVal ? 'bg-muted/40 text-muted-foreground' : ''}
                                    title={!isCustomized ? 'Klicken zum Individualisieren für diesen Link' : ''}
                                    onFocus={() => {
                                      if (!customVal && tenantVal) {
                                        setLinkSettings(s => ({
                                          ...s, platform_urls: { ...s.platform_urls, [key]: tenantVal },
                                        }));
                                      }
                                    }}
                                  />
                                  {!isCustomized && tenantVal && (
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground/50 pointer-events-none group-focus-within:hidden">
                                      → Klicken zum Anpassen
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <Button onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Speichern
                </Button>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="card-shadow border-red-200">
              <CardHeader>
                <CardTitle className="text-base text-red-700">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Link {link.status === 'active' ? 'deaktivieren' : 'aktivieren'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {link.status === 'active'
                        ? 'Der Link ist nicht mehr aufrufbar — bestehende Bewertungen bleiben erhalten.'
                        : 'Der Link wird wieder für Bewerter zugänglich gemacht.'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleToggleStatus}
                    disabled={togglingStatus}
                    className={link.status === 'active'
                      ? 'border-red-200 text-red-600 hover:bg-red-50'
                      : 'border-green-200 text-green-600 hover:bg-green-50'}
                  >
                    {togglingStatus
                      ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      : <Power className="w-4 h-4 mr-2" />}
                    {link.status === 'active' ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ─── Review Detail Sheet ─────────────────────────────────────── */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Bewertungsdetails</SheetTitle>
            </SheetHeader>
            {selectedReview && (
              <div className="mt-6 space-y-6">
                {/* Impulse vs. Differentiated */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50 text-center">
                    <p className="text-xs text-muted-foreground mb-2">Impulsbewertung</p>
                    <div className="flex justify-center mb-1">{renderStars(selectedReview.overall_rating)}</div>
                    <p className="text-2xl font-bold">{selectedReview.overall_rating}/5</p>
                    <p className="text-xs text-muted-foreground mt-1">Erster Eindruck</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/5 border border-accent/20 text-center">
                    <p className="text-xs text-muted-foreground mb-2">Differenzierte Bewertung</p>
                    {getCategoryAverage(selectedReview) ? (
                      <>
                        <div className="flex justify-center mb-1">{renderStars(Math.round(parseFloat(getCategoryAverage(selectedReview))))}</div>
                        <p className="text-2xl font-bold">{getCategoryAverage(selectedReview)}/5</p>
                        {parseFloat(getCategoryAverage(selectedReview)) > selectedReview.overall_rating && (
                          <p className="text-xs text-green-600 mt-1">+{(parseFloat(getCategoryAverage(selectedReview)) - selectedReview.overall_rating).toFixed(1)} nach Differenzierung</p>
                        )}
                        {parseFloat(getCategoryAverage(selectedReview)) < selectedReview.overall_rating && (
                          <p className="text-xs text-yellow-600 mt-1">{(parseFloat(getCategoryAverage(selectedReview)) - selectedReview.overall_rating).toFixed(1)} nach Differenzierung</p>
                        )}
                        {parseFloat(getCategoryAverage(selectedReview)) === selectedReview.overall_rating && (
                          <p className="text-xs text-muted-foreground mt-1">Keine Abweichung</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground mt-2">Keine Kategorien bewertet</p>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Datum</p>
                  <p className="font-medium">{format(new Date(selectedReview.createdAt), 'dd. MMMM yyyy, HH:mm', { locale: de })} Uhr</p>
                </div>

                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Typ</p>
                    <Badge variant={selectedReview.path === 'positive' ? 'default' : 'secondary'}>
                      {selectedReview.path === 'positive' ? 'Positive Bewertung' : 'Kritische Bewertung'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Stimmung</p>
                    {(() => {
                      const s = getSentiment(selectedReview.overall_rating);
                      return <div className={`flex items-center gap-1 ${s.color}`}><s.icon className="w-4 h-4" /><span className="text-sm font-medium">{s.label}</span></div>;
                    })()}
                  </div>
                </div>

                {/* Categories */}
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Kategorien</p>
                  <div className="space-y-2">
                    {['Kommunikation', 'Service', 'Abrechnung', 'Reaktionszeit', 'Kompetenz'].map((label, i) => {
                      const cat = selectedReview.categories?.find(c => c.label === label);
                      return (
                        <div key={i} className="flex items-center justify-between">
                          <span className={`text-sm ${cat ? '' : 'text-muted-foreground'}`}>{label}</span>
                          {cat ? renderStars(cat.rating) : <span className="text-xs text-muted-foreground italic">Nicht bewertet</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedReview.free_text && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Freitext</p>
                    <p className="p-3 rounded-lg bg-muted text-sm">{selectedReview.free_text}</p>
                  </div>
                )}
                {selectedReview.rewritten_text && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Umformulierter Text</p>
                    <p className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm">{selectedReview.rewritten_text}</p>
                  </div>
                )}
                {selectedReview.fairness && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Sachlichkeitsanalyse</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.fairness_score}%</p>
                        <p className="text-xs text-muted-foreground">Sachlichkeit</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.emotion_score}%</p>
                        <p className="text-xs text-muted-foreground">Emotion</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.risk_score}%</p>
                        <p className="text-xs text-muted-foreground">Risiko</p>
                      </div>
                    </div>
                  </div>
                )}
                {selectedReview.verification?.email && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Kontakt</p>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedReview.verification.email}</span>
                    </div>
                  </div>
                )}
                {selectedReview.reaction_gate?.chosen && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Reaktionswunsch</p>
                    <Badge variant="outline">
                      {selectedReview.reaction_gate.chosen === 'send_to_hv' ? 'Rückmeldung gewünscht' : 'Direkt gesendet'}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* ─── Rename Dialog ───────────────────────────────────────────── */}
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle>Link umbenennen</DialogTitle></DialogHeader>
            <div className="py-3">
              <Input value={renameName} onChange={e => setRenameName(e.target.value)} placeholder="Neuer Name" autoFocus />
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setRenameOpen(false)}>Abbrechen</Button>
              <Button onClick={handleRename} disabled={!renameName.trim()}>Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      <HVFooter />
    </div>
  );
}
